import { alpha, type Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { getDensityConfig } from 'src/helpers/densityConfig';
import type { DensityType } from 'src/types/store.types';

interface StyleProps {
  isOnTwoupView?: boolean;
  density: DensityType;
}

const styles = makeStyles((theme: Theme) => ({
  galleryIcon: {
    cursor: 'pointer',
    '& svg': {
      color: theme.palette.secondary.main,
      backgroundColor: theme.palette.background.default,
    },
  },
  galleryTitleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '1em',
  },
  galleryTitle: {
    color: theme.palette.secondary.main,
    fontSize: '1.3em',
  },
  collectionDividerIndicator: {
    borderBottom: `0.2rem solid ${theme.palette.secondary.main}`,
    width: '100%',
  },
  mainmediacardframe: (props: StyleProps) => ({
    backgroundColor: theme.palette.background.paper,
    margin: 'auto',
    width: '100%',
    minHeight: props.isOnTwoupView ? '920px' : undefined,
    height: 'auto',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column' as const,
  }),
  cardMedia: (props: StyleProps) => {
    const config = getDensityConfig(props.density);
    return {
      position: 'relative' as const,
      // TwoUp is a detail view — don't shrink the thumbnail with density
      minHeight: props.isOnTwoupView ? 920 : config.thumbnailMinHeight,
    };
  },
  cardTitle: {
    cursor: 'pointer',
    color: theme.palette.text.primary,
    textDecoration: 'none',
  },
  breakoutTitle: (props: StyleProps) => {
    const config = getDensityConfig(props.density);
    return {
      display: 'block',
      fontSize: config.titleFontSize,
      fontWeight: 600,
      color: theme.palette.text.primary,
      textDecoration: 'none',
      cursor: 'pointer',
      whiteSpace: 'nowrap' as const,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      marginBottom: theme.spacing(config.titleMarginBottom),
      '&:hover': {
        color: theme.palette.secondary.main,
      },
    };
  },
  cardSubtitle: {
    whiteSpace: 'normal',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordWrap: 'break-word',
  },
  cardContent: (props: StyleProps) => {
    const config = getDensityConfig(props.density);
    return {
      padding: config.cardContentPadding,
    };
  },
  cardActions: {
    padding: '0 8px',
  },
  expandMore: {
    margin: 0,
    fontSize: '1.2rem',
  },
  collapse: {},
  expand: {
    padding: '2px 4px',
    transform: 'rotate(0deg)',
    marginLeft: 'auto',
    float: 'right',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: 'rotate(180deg)',
  },
  divider: {
    height: '2px',
    width: '92%',
    backgroundColor: theme.palette.primary.main,
  },
  transcriptResults: {
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: 280,
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 1.5),
    '&::-webkit-scrollbar': {
      width: 4,
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'transparent',
      borderRadius: 4,
      transition: 'background-color 200ms ease',
    },
    '&:hover::-webkit-scrollbar-thumb': {
      backgroundColor: alpha(theme.palette.text.primary, 0.15),
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: alpha(theme.palette.text.primary, 0.25),
    },
    // biome-ignore lint/suspicious/noExplicitAny: scrollbarWidth not in MUI's CSS types
    scrollbarWidth: 'thin' as any,
    scrollbarColor: `transparent transparent`,
    '&:hover': {
      scrollbarColor: `${alpha(theme.palette.text.primary, 0.15)} transparent`,
    },
  },
  transcriptHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.5, 0),
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.disabled,
    fontSize: '0.7rem',
    fontWeight: 500,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    '& svg': {
      fontSize: 14,
    },
  },
  transcriptExpression: {
    padding: theme.spacing(0.75, 1),
    borderRadius: 6,
    cursor: 'pointer',
    transition: 'background-color 120ms ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.secondary.main, 0.08),
    },
    '& + &': {
      marginTop: theme.spacing(0.25),
    },
  },
  transcriptTimecode: {
    color: theme.palette.secondary.light,
    fontSize: '0.75rem',
    fontFamily: 'monospace',
    lineHeight: 1.4,
  },
  transcriptText: {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    cursor: 'pointer',
    display: 'block',
    width: '100%',
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    lineHeight: 1.5,
    textAlign: 'left' as const,
  },
  fieldTitle: (props: StyleProps) => {
    const config = getDensityConfig(props.density);
    return {
      color: theme.palette.text.disabled,
      fontSize: config.fieldLabelFontSize,
      lineHeight: 1.4,
      whiteSpace: 'nowrap' as const,
      flexShrink: 0,
    };
  },
  fieldSep: {
    color: theme.palette.text.disabled,
    flexShrink: 0,
    fontSize: '0.5rem',
    margin: '0 5px',
    lineHeight: 1,
  },
  titleAndValue: (props: StyleProps) => {
    const config = getDensityConfig(props.density);
    return {
      display: 'flex',
      alignItems: 'baseline',
      gap: '0px',
      padding: config.fieldRowPadding,
      margin: config.fieldRowMargin,
      overflow: 'hidden',
    };
  },
  highlightedText: (props: StyleProps) => {
    const config = getDensityConfig(props.density);
    return {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
      flex: 1,
      minWidth: 0,
      fontSize: config.fieldValueFontSize,
    };
  },
  emptyValue: (props: StyleProps) => {
    const config = getDensityConfig(props.density);
    return {
      color: alpha(theme.palette.text.primary, 0.12),
      fontSize: config.fieldValueFontSize,
      letterSpacing: 2,
    };
  },
  fallbackIcon: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
  },
}));

export default styles;
